import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import type { IOrganizationChangeCallbackParams } from '@frontend/components/agromarket/components/MainHeader'
import { MainHeader } from '@frontend/components/agromarket/components/MainHeader'
import { EAccessControlValue } from '@frontend/components/agromarket/core/access-control'
import { useQueryClient } from '@tanstack/react-query'
import { useMultiCart } from '@/api/hooks/inputs/cart'
import { history } from '@/browserHistory'
import { useAppAccessControl } from '@/components/composed/AppAccessControlProvider'
import { openModal } from '@/components/composed/Modal/actions'
import { isStartAuthModalAvailable, useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { shouldHideHeader } from '@/logic/app/reducer'
import { setJwt } from '@/logic/auth/actions'
import { fetchMultiCart } from '@/logic/cart/actions'
import { getNotificationsValues } from '@/logic/notifications/reducer'
import { fetchUserProfile } from '@/logic/profile/actions'
import type { RootState } from '@/redux/interfaces'
import { EUserProfileRoute } from '@/types'
import { useEffectAfterMount } from '@/utils/hooks'
import { createRouterLink } from '@/utils/url'
import './styles.scss'

const mapState = (state: RootState) => ({
  notifications: getNotificationsValues()(state),
  shouldHide: shouldHideHeader()(state),
})

export const AppHeader = () => {
  const { notifications, shouldHide } = useSelector(mapState)
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { profilePath, isAuthenticated, logoutUser, openAuthModal } = useAuthenticateUser()
  const { pathname } = useLocation()

  const { resolve } = useAppAccessControl()
  const isCartShown = resolve('header.cart') === EAccessControlValue.VISIBLE

  const { data } = useMultiCart()
  const { cartsItemsNumber } = data

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchUserProfile())
    //eslint-disable-next-line
  }, [isAuthenticated])

  useEffectAfterMount(() => {
    if (isCartShown) dispatch(fetchMultiCart())
  }, [isCartShown, dispatch])

  const handleLogin = async () => {
    if (profilePath && isAuthenticated) {
      history.push(profilePath)
    } else if (isAuthenticated) {
      logoutUser()
      queryClient.clear()
    } else {
      const shouldUseStartAuthModal = await isStartAuthModalAvailable()

      const dialogId = shouldUseStartAuthModal ? 'StartAuthModal' : 'AuthModal'

      openAuthModal(undefined, dialogId)
    }
  }

  const onOrganizationChange = useCallback(
    (params: IOrganizationChangeCallbackParams) => {
      const { organizationName, token } = params

      if (organizationName && token) {
        dispatch(setJwt({ jwt: token }))

        const onCloseAction = () => {
          const isSomeProfilePage = pathname?.startsWith(EUserProfileRoute.main)

          if (isSomeProfilePage) {
            location.href = createRouterLink(EUserProfileRoute.main)
          } else {
            location.reload()
          }
        }

        dispatch(
          openModal({
            options: {
              dialogId: 'SuccessChangeActiveOrganisationModal',
              onCloseAction,
            },
            contentOptions: {
              titleId: 'successChangeActiveOrganisationModal.title',
              titleValues: { orgName: organizationName },
              messageId: 'successChangeActiveOrganisationModal.subtitle',
              buttonsBlockStyle: 'successChangeActiveOrganisationSuccessModal__button',
              buttons: [
                {
                  textId: 'successChangeActiveOrganisationModal.button',
                  modifiers: ['green'],
                  onClick: onCloseAction,
                },
              ],
            },
          }),
        )
      }
    },
    [dispatch, pathname],
  )

  if (shouldHide) return null

  return (
    <MainHeader
      yandexMaps={{ apiKey: process.env.YANDEX_API_KEY }}
      className="mainHeader"
      organization={{
        onOrganizationChange,
      }}
      cart={{ showCart: isCartShown, numberOfItemsInCart: cartsItemsNumber }}
      notifications={{
        unreadNotifications: notifications.unreadNotificationsCount,
        unreadChatMessages: notifications.unreadChatMessagesCount,
      }}
      user={{ isUserLoggedIn: isAuthenticated }}
      onLogin={handleLogin}
    />
  )
}
